import requestLoyaltyTierUpgradeClaim, { type LoyaltyTierUpgradeClaimResult } from 'api/loyalty/requestLoyaltyTierUpgradeClaim'
import { getTierUpgradeClaimErrorDetails } from 'loyalty/pages/tierUpgradeCampaign/useTierUpgradeClaimErrorMessage'
import { markCampaignClaimed } from 'loyalty/tierUpgradeCampaign/tierUpgradeClaimHistory'
import { broadcastTierUpgradeClaimed } from 'loyalty/tierUpgradeCampaign/tierUpgradeCampaignTabSync'
import { isAlreadyClaimedError } from 'loyalty/tierUpgradeCampaign/emailDomainEligibility'

const inFlightClaims = new Map<string, Promise<LoyaltyTierUpgradeClaimResult>>()

/**
 * Claims an email-domain tier upgrade campaign (no code). Concurrent callers for the same campaign
 * share one request: the backend only counts a claim once it completes, so overlapping requests
 * would otherwise both run and the second would fail with TIER_ALREADY_HIGHER. Once the tier is
 * granted (or found to be already granted), the customer's other open tabs are told to reload it,
 * and the claim is recorded in this page load's claim history so it isn't attempted again.
 */
export function claimTierUpgradeCampaign(campaignId: string, memberId?: string): Promise<LoyaltyTierUpgradeClaimResult> {
  const existing = inFlightClaims.get(campaignId)
  if (existing) return existing

  const claim = requestLoyaltyTierUpgradeClaim(campaignId)
    .then(
      (result) => {
        if (result.claimed) {
          markCampaignClaimed(memberId, campaignId)
          broadcastTierUpgradeClaimed(campaignId)
        }
        return result
      },
      (err) => {
        const { errorCode } = getTierUpgradeClaimErrorDetails(err)
        if (isAlreadyClaimedError(errorCode)) markCampaignClaimed(memberId, campaignId)
        if (isAlreadyClaimedError(errorCode) || errorCode === 'TIER_ALREADY_HIGHER') broadcastTierUpgradeClaimed(campaignId)
        throw err
      },
    )
    .finally(() => {
      inFlightClaims.delete(campaignId)
    })
  inFlightClaims.set(campaignId, claim)
  return claim
}

/** The tier a successful claim granted, falling back to the campaign tier. */
export function getClaimedTier(result: LoyaltyTierUpgradeClaimResult, fallbackTier: App.LoyaltyTier): App.LoyaltyTier {
  const tierUpgradeAction = result.actions.find(action => action.type === 'TIER_UPGRADE' && action.status === 'COMPLETED')
  return (tierUpgradeAction?.result?.newTier ?? fallbackTier) as App.LoyaltyTier
}
